// PDF text field extraction templates for major Turkish insurance companies.
// Each template supplies label variants to search for. Fields are extracted by
// running each label as an anchored regex against the raw PDF text.

const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Diacritic-insensitive normalizer: 1-to-1 char replacement (preserves offsets).
const norm = (s) =>
  (s || "")
    .replace(/İ/g, "I").replace(/ı/g, "i")
    .replace(/Ş/g, "S").replace(/ş/g, "s")
    .replace(/Ğ/g, "G").replace(/ğ/g, "g")
    .replace(/Ü/g, "U").replace(/ü/g, "u")
    .replace(/Ö/g, "O").replace(/ö/g, "o")
    .replace(/Ç/g, "C").replace(/ç/g, "c")
    .replace(/Â/g, "A").replace(/â/g, "a")
    .replace(/Î/g, "I").replace(/î/g, "i")
    .replace(/Û/g, "U").replace(/û/g, "u");

const num = (s) => {
  if (!s) return "";
  const m = String(s).match(/-?\d[\d.,]*/);
  if (!m) return "";
  let v = m[0];
  const hasComma = v.includes(",");
  const hasDot = v.includes(".");
  if (hasComma && hasDot) {
    // Whichever appears LAST is the decimal separator
    const lastComma = v.lastIndexOf(",");
    const lastDot = v.lastIndexOf(".");
    if (lastComma > lastDot) {
      // Turkish format: "." thousand, "," decimal
      v = v.replace(/\./g, "").replace(",", ".");
    } else {
      // Anglo format: "," thousand, "." decimal
      v = v.replace(/,/g, "");
    }
  } else if (hasComma) {
    // Only comma → treat as decimal
    v = v.replace(",", ".");
  }
  return v;
};

const dateToIso = (d) => {
  if (!d) return "";
  const m = d.match(/(\d{1,2})[./-](\d{1,2})[./-](\d{4})/);
  if (!m) return "";
  return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
};

// Line-based value finder with strict label matching to avoid picking up phrases
// from prose (e.g. "sigorta ettiren, lehtar…") as if they were labels.
// A label matches when the line is one of:
//   • "<label>"                → value = next non-empty line
//   • "<label>:"               → value = next non-empty line
//   • "<label>: <value>"       → value = <value>
//   • "... <label>: <value>"   → value = <value>  (mid-line, requires colon)
// The label MUST be followed by a colon (":" or "：") or by end-of-line — never
// by a comma or other prose punctuation.
const findAfter = (rawText, labels, len = 90, negativeFilter = null) => {
  const rawLines = rawText.split(/\n/);
  const normLines = rawLines.map(norm);

  const tryNextLines = (fromIdx) => {
    for (let j = fromIdx + 1; j < Math.min(fromIdx + 4, rawLines.length); j++) {
      const line = rawLines[j].trim();
      if (!line) continue;
      const candidate = line.slice(0, len).trim();
      if (!candidate) return null;
      if (negativeFilter && negativeFilter(candidate)) return null;
      return candidate;
    }
    return null;
  };

  const clean = (s) => s.slice(0, len).trim();

  for (const label of labels) {
    const nLabel = norm(label);
    // Pass 1: label at line start (with colon-or-end)
    const startAlone = new RegExp("^\\s*" + escapeRe(nLabel) + "\\s*[:：]?\\s*$", "i");
    const startWithVal = new RegExp("^\\s*" + escapeRe(nLabel) + "\\s*[:：]\\s*(.+)$", "i");
    // Pass 2: label mid-line with mandatory colon
    const midWithVal = new RegExp("(?:^|[\\s(/,])" + escapeRe(nLabel) + "\\s*[:：]\\s*(\\S[^\\n\\r]*)", "i");

    for (let i = 0; i < normLines.length; i++) {
      // Try start-of-line-alone (value on next line)
      if (startAlone.test(normLines[i])) {
        const val = tryNextLines(i);
        if (val) return val;
        continue;
      }
      // Try start-of-line with same-line value
      const m2 = normLines[i].match(startWithVal);
      if (m2 && m2[1]) {
        const v = clean(m2[1]);
        if (v && (!negativeFilter || !negativeFilter(v))) return v;
        continue;
      }
      // Try mid-line label:value
      const m3 = normLines[i].match(midWithVal);
      if (m3 && m3[1]) {
        // For mid-line, we need to slice raw line at the same offset
        // Simpler: re-run on raw line to get value with original characters
        const midWithValRaw = new RegExp("(?:^|[\\s(/,])" + escapeRe(nLabel) + "\\s*[:：]\\s*(\\S[^\\n\\r]*)", "i");
        const rawMatch = rawLines[i].match(midWithValRaw);
        const val = clean((rawMatch && rawMatch[1]) || m3[1]);
        if (val && (!negativeFilter || !negativeFilter(val))) return val;
      }
    }
  }
  return "";
};

// Marketing / boilerplate phrases that must NOT be treated as a real customer name or field value.
const MARKETING_RE = /(Sadakat|Programı|Programi|Hoş Geldiniz|Hos Geldiniz|Değerli|Degerli|Sigortalımız|Sigortalimiz|Bilgilendirme|Genel Şartlar|Genel Sartlar|SİGORTA POLİÇESİ|SIGORTA POLICESI|Sayın Sigortal|Sayin Sigortal|Kanunu|Yönetmelik|Yonetmelik|Bilgileri|ETTİREN|ETTIREN)/i;
// Derived from COMPANIES below (single source of truth). Matches lines that look
// like a company signature block (e.g. "DOĞA SİGORTA", "ANADOLU SİGORTA A.Ş.").
let COMPANY_SIGNATURE_RE = /^$/;  // placeholder; rebuilt after COMPANIES is declared

// Skip the marketing preface — real policy data starts after certain markers.
const skipPreface = (text) => {
  const markers = [
    /Poliçe\s*(?:\/\s*Yenileme\s*\/\s*Zeyil\s*\/\s*Ürün\s*)?No\b/i,
    /Police\s*No\b/i,
    /KARAYOLLARI MOTORLU/i,
    /Başlama Tarihi/i,
    /Baslama Tarihi/i,
    /Başlangıç Tarihi/i,
    /Baslangic Tarihi/i,
    /Tanzim Tarihi/i,
  ];
  const normalized = norm(text);
  let minIdx = -1;
  for (const re of markers) {
    const m = normalized.match(re);
    if (m && (minIdx === -1 || m.index < minIdx)) minIdx = m.index;
  }
  return minIdx >= 0 ? text.slice(minIdx) : text;
};

const findDates = (text) => text.match(/\b\d{1,2}[./-]\d{1,2}[./-]\d{4}\b/g) || [];

// Company-specific configurations. Order matters — check more specific/unique brands first
// to avoid false positives (e.g. "Türkiye Sigorta Reasürans..." in KVKK footer).
const COMPANIES = [
  { id: "doga", name: "Doğa Sigorta", keywords: ["Doğa Sigorta A.Ş", "DOĞA SİGORTA A.Ş", "Doğa Sigorta", "DOĞA SİGORTA", "Doga Sigorta", "DOGA SİGORTA"] },
  { id: "anadolu", name: "Anadolu Sigorta", keywords: ["Anadolu Sigorta", "Anadolu Anonim"] },
  { id: "allianz", name: "Allianz Sigorta", keywords: ["Allianz Sigorta", "ALLIANZ"] },
  { id: "aksigorta", name: "Aksigorta", keywords: ["Aksigorta A.Ş", "Aksigorta", "AKSİGORTA"] },
  { id: "hdi", name: "HDI Sigorta", keywords: ["HDI Sigorta", "HDI Sıgorta"] },
  { id: "sompo", name: "Sompo Sigorta", keywords: ["Sompo Sigorta", "Sompo Japan", "SOMPO SİGORTA"] },
  { id: "axa", name: "AXA Sigorta", keywords: ["AXA Sigorta"] },
  { id: "ray", name: "Ray Sigorta", keywords: ["Ray Sigorta", "RAY SİGORTA"] },
  { id: "neova", name: "Neova Sigorta", keywords: ["Neova Sigorta", "NEOVA"] },
  { id: "zurich", name: "Zurich Sigorta", keywords: ["Zurich Sigorta", "Zürich Sigorta"] },
  { id: "groupama", name: "Groupama Sigorta", keywords: ["Groupama Sigorta", "Groupama"] },
  { id: "mapfre", name: "MAPFRE Sigorta", keywords: ["MAPFRE Sigorta", "Mapfre Sigorta", "mapfre Sigorta"] },
  { id: "ankara", name: "Ankara Sigorta", keywords: ["Ankara Sigorta", "ANKARA SİGORTA"] },
  { id: "quick", name: "Quick Sigorta", keywords: ["Quick Sigorta", "QUICK SİGORTA"] },
  { id: "corpus", name: "Corpus Sigorta", keywords: ["Corpus Sigorta", "CORPUS SİGORTA"] },
  { id: "gulf", name: "Gulf Sigorta", keywords: ["Gulf Sigorta", "GULF SİGORTA"] },
  { id: "unico", name: "Unico Sigorta", keywords: ["Unico Sigorta", "UNICO SİGORTA"] },
  { id: "orient", name: "Orient Sigorta", keywords: ["Orient Sigorta", "ORIENT SİGORTA"] },
  { id: "turk-nippon", name: "Türk Nippon Sigorta", keywords: ["Türk Nippon Sigorta", "TÜRK NİPPON"] },
  // ANA Sigorta uses the distinctive "ANA GENİŞLETİLMİŞ KASKO" product name; keep after Anadolu.
  { id: "ana", name: "ANA Sigorta", keywords: ["ANA GENİŞLETİLMİŞ KASKO", "ANA SİGORTA A.Ş", "ANA SİGORTA"] },
  { id: "emaa", name: "EMAA Sigorta", keywords: ["EMAA SİGORTA A.Ş", "EMAA Sigorta", "EMAA SİGORTA"] },
  // Türkiye Sigorta uses A.Ş. as differentiator (else matches "Türkiye Sigorta Birliği/Reasürans")
  { id: "turkiye", name: "Türkiye Sigorta", keywords: ["Türkiye Sigorta A.Ş", "TÜRKİYE SİGORTA A.Ş", "Türkiye Sigorta AŞ"] },
];

// Broad set of label variants — try many because Turkish policies vary by company
const COMMON_LABELS = {
  policeNo: [
    "Poliçe / Yenileme / Zeyil / Ürün No",
    "Poliçe/Yenileme/Zeyil/Ürün No",
    "Poliçe Numarası", "Poliçe No", "Police Numarası", "Police No",
    "Polica No", "Polica Numarası", "Yenileme Poliçe No",
    "Ürün No", "Urun No",
    "POLİÇENİN NUMARASI", "NUMARASI",
  ],
  customer: [
    "SİGORTALININ ADI SOYADI", "SIGORTALININ ADI SOYADI",
    "Sigortalı Adı Soyadı", "Sigortalı Adı", "Sigortalı / Sigorta Ettiren",
    "Sigortalı Ünvanı", "Sigorta Ettiren Adı", "Sigorta Ettiren",
    "Adı Soyadı", "ADI SOYADI",
    "Ad Soyad", "Ad/Soyad", "Ad-Soyad",
    "Müşteri Adı", "Müşteri",
    "Sigortalı",
    "Sayın;", "Sayın",
  ],
  premium: [
    "BRÜT PRİMİ", "BRUT PRIMI",
    "Toplam Brüt Prim", "Brüt Prim (TL)", "Brüt Prim(TL)", "Brüt Prim",
    "Toplam Prim (TL)", "Toplam Prim", "Ödenecek Prim", "Ödenecek Tutar",
    "Prim Tutarı", "Genel Toplam", "Prim (TL)",
  ],
  company: [
    "Sigorta Şirketi", "Şirket Ünvanı", "Ticaret Ünvanı", "Şirket",
  ],
  plate: [
    "Plaka No", "Plaka Numarası", "Araç Plakası", "Plaka", "PLAKA",
  ],
  datesRange: [
    "BAŞLANGICI VE BİTİMİ", "Başlangıç ve Bitim", "Başlangıç - Bitiş",
  ],
};

// Turkish license plate patterns. Handles both spaced and joined forms:
//   "34 ABC 1234", "34 ABC1234", "34ABC 1234", "34ABC1234"
//   "06 BLK 543", "06BLK543"
//   "34 A 12", "34A12"
// Also handles Turkish OCR quirks: I→1, O→0 confusion inside the letter block
// (we still accept as-is; user can correct).
// Province codes: 01-81. Letters: 1-3 Latin uppercase. Suffix: 2-4 digits.
const PLATE_RE_STRICT = /\b(0[1-9]|[1-7][0-9]|8[01])\s*([A-Z]{1,3})\s*([0-9]{2,4})\b/;
// Broader fallback (accepts any 2 digits as province — some templates print leading zeros oddly)
const PLATE_RE_LOOSE = /\b([0-9]{2})\s*([A-Z]{1,3})\s*([0-9]{2,4})\b/;

// Values that must be rejected: TC identity numbers, VAT numbers, policy numbers etc.
// Reject if the raw match spans a longer digit run (e.g. "34123456789" catches 34 A 12 wrongly? No — needs letters between).
// So the shape itself is fairly safe. Just filter out obviously wrong ones like province==00.
function isPlateShape(digits1, letters, digits2) {
  const p = parseInt(digits1, 10);
  if (p < 1 || p > 81) return false;
  // Reject 4+2 digit combos where letters look like OCR of digits (e.g. "1234 O 12"? — filter len2 outputs)
  return letters.length >= 1 && letters.length <= 3 && digits2.length >= 2 && digits2.length <= 4;
}

function extractPlate(text) {
  if (!text) return "";
  // 1) Try labelled match — the "Plaka" label is a strong signal, look within 60 chars after.
  for (const label of COMMON_LABELS.plate) {
    const idx = text.indexOf(label);
    if (idx >= 0) {
      const window = text.slice(idx + label.length, idx + label.length + 60).toUpperCase();
      // Skip common separators (: - . |) at start
      const cleaned = window.replace(/^[\s:\-.|]+/, "");
      const m = cleaned.match(PLATE_RE_LOOSE);
      if (m && isPlateShape(m[1], m[2], m[3])) {
        return `${m[1]} ${m[2]} ${m[3]}`;
      }
    }
  }
  // 2) Strict fallback: scan whole document for a shape with a valid province code (01-81).
  const upper = text.toUpperCase();
  const strict = upper.match(PLATE_RE_STRICT);
  if (strict) return `${strict[1]} ${strict[2]} ${strict[3]}`;
  // 3) Loose fallback for edge cases (province out of range but shape matches).
  const loose = upper.match(PLATE_RE_LOOSE);
  if (loose && isPlateShape(loose[1], loose[2], loose[3])) {
    return `${loose[1]} ${loose[2]} ${loose[3]}`;
  }
  return "";
}

const BRANCH_KEYWORDS = {
  "Trafik": ["Karayolları Motorlu Araçlar Zorunlu Mali Sorumluluk", "Zorunlu Trafik", "Karayolları Motorlu", "TRAFİK SİGORTASI", "Trafik Sigortası", "Trafik"],
  "Kasko": ["Genişletilmiş Kasko", "Motorlu Kara Taşıtları Kasko", "Kasko Sigortası", "KASKO", "Kasko"],
  "DASK": ["Zorunlu Deprem Sigortası", "Doğal Afet Sigortaları", "DASK"],
  "Konut": ["Konut Sigortası", "KONUT", "Yangın Sigortası"],
  "Sağlık": ["Özel Sağlık Sigortası", "Sağlık Sigortası", "SAĞLIK"],
  "Tamamlayıcı Sağlık": ["Tamamlayıcı Sağlık Sigortası", "TSS", "Tamamlayıcı Sağlık", "Tamamlayıcı"],
  "İşyeri": ["İşyeri Sigortası", "İŞYERİ SİGORTASI", "İşyeri"],
  "Ferdi Kaza": ["Ferdi Kaza Sigortası", "FERDİ KAZA", "Ferdi Kaza"],
  "Nakliyat": ["Nakliyat Sigortası", "Emtia Nakliyat", "NAKLİYAT"],
};

export const KNOWN_COMPANIES = COMPANIES.map((c) => c.name);

// Per-field confidence used by the import preview to highlight low-confidence
// values in yellow so the user can review/edit before saving.
// Returns { policeNo, customer, branch, company, start, end, premium } → "high"|"low".
export function assessConfidence(parsed) {
  const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
  const nameLooksReal = (v) => {
    const s = String(v || "").trim();
    if (!s || s.length < 3) return false;
    // At least one space AND all tokens are alphabetic (Turkish letters allowed)
    if (!/\s/.test(s)) return false;
    if (!/^[A-Za-zÇĞİıÖŞÜçğıöşü .'-]+$/.test(s)) return false;
    // Must not be a boilerplate label like "Adres" or "Sigortalı"
    if (/^(adres|sigortali|sigortalı|sigorta|adı soyadı|adi soyadi)$/i.test(s)) return false;
    return true;
  };
  const policyOk = (v) => {
    const s = String(v || "").trim();
    return s.length >= 5 && /[0-9]/.test(s);
  };
  const premiumOk = (v) => {
    const n = Number(v);
    return !Number.isNaN(n) && n > 0;
  };
  return {
    policeNo: policyOk(parsed.policeNo) ? "high" : "low",
    customer: nameLooksReal(parsed.customer) ? "high" : "low",
    branch: parsed.branch ? "high" : "low",
    company: parsed.company ? "high" : "low",
    start: isIsoDate(parsed.start) ? "high" : "low",
    end: isIsoDate(parsed.end) ? "high" : "low",
    premium: premiumOk(parsed.premium) ? "high" : "low",
  };
}

// Rebuild COMPANY_SIGNATURE_RE from COMPANIES first-token (the brand word before "Sigorta").
{
  const brands = Array.from(new Set(
    COMPANIES.flatMap((c) => [
      c.name.split(/\s+/)[0],
      norm(c.name.split(/\s+/)[0]),
      c.name.split(/\s+/).slice(0, 2).join(" "),
    ])
  )).filter(Boolean).map(escapeRe);
  COMPANY_SIGNATURE_RE = new RegExp(
    "^(?:" + brands.join("|") + ")\\s+(?:SİGORTA|SIGORTA)(?:\\s+A\\.?Ş\\.?|\\s*A\\.Ş\\.?)?",
    "i"
  );
}

const detectCompany = (text) => {
  const lc = norm(text).toLowerCase();
  for (const c of COMPANIES) {
    if (c.keywords.some((k) => lc.includes(norm(k).toLowerCase()))) {
      return c;
    }
  }
  return null;
};

const detectBranch = (text, branchList) => {
  const lc = norm(text).toLowerCase();
  for (const [branch, keys] of Object.entries(BRANCH_KEYWORDS)) {
    if (branchList.some((b) => b.name === branch) && keys.some((k) => lc.includes(norm(k).toLowerCase()))) {
      return branch;
    }
  }
  for (const b of branchList) {
    if (lc.includes(norm(b.name).toLowerCase())) return b.name;
  }
  return branchList[0]?.name || "";
};

// Company-specific parser overrides fix layouts the generic label-based parser
// cannot handle (multi-column table rows, stacked colon-prefixed values,
// right-column names flattened by PDF.js, image-only cover pages).
const COMPANY_OVERRIDES = {
  sompo: (raw, base) => {
    // Real Sompo layout (PDF.js flattens 2-line header + wrapping data cell):
    //   MÜŞTERİ NO  YENİLEME NO  BİTİŞ TARİHİ EK NO  BAŞLAMA TARİHİ POLİÇE NO ACENTE NO
    //   100783904  16/09/2027 0 0  16/09/2026 420103
    //   SÜRE
    //   311000635940798  365 Gün
    // Policy no is a very specific 12-18 digit number that wraps to its own line
    // right after the "SÜRE" break.
    const pn = raw.match(/S[ÜU]RE\s*\n\s*(\d{12,18})\b/i);
    if (pn) base.policeNo = pn[1];
    // Dates: pick the two dd/mm/yyyy dates on the header data row.
    // Order in real layout: BİTİŞ appears BEFORE BAŞLAMA in the header (weird!)
    // so on data row date1=bitiş date2=başlama.
    const drow = raw.match(/M[ÜU][ŞS]TER[İI]\s+NO[\s\S]{0,200}?ACENTE\s+NO\s*\n\s*\d+\s+(\d{2}\/\d{2}\/\d{4})[\s\d]+?(\d{2}\/\d{2}\/\d{4})/i);
    if (drow) {
      base.end = dateToIso(drow[1]);
      base.start = dateToIso(drow[2]);
    }
    const cm = raw.match(/ADI\s+SOYADI\s*\/?\s*[ÜU]?NVANI?\s*:\s*([^\n]+)/i);
    if (cm) {
      const v = cm[1].replace(/\s{2,}/g, " ").trim();
      if (v && !MARKETING_RE.test(v)) base.customer = v.slice(0, 60);
    }
    const pm = raw.match(/Toplam\s+Br[üu]t\s+Prim\s*:\s*([\d.,]+)/i);
    if (pm) base.premium = num(pm[1]);
    return base;
  },

  ana: (raw, base) => {
    // Table row example: "034216  94871185  -  -  03.09.2026  365  03.09.2026  03.09.2027"
    // Between poliçe no and first date can be one or more non-numeric fillers
    // (dash, "0", etc.). Match: acente, police, then anything non-date, then 3 dates.
    const tr = raw.match(/Acente\s+No\s+Poli[çc]e\s+No[\s\S]{0,200}?Biti[şs]\s+Tarihi[\s\S]{0,80}?\n\s*(\d+)\s+(\d+)\s+(?:[^\d\n]+\s+)*?(\d{2}\.\d{2}\.\d{4})\s+\d+\s+(\d{2}\.\d{2}\.\d{4})\s+(\d{2}\.\d{2}\.\d{4})/i);
    if (tr) {
      base.policeNo = tr[2];
      // Format: tanzimDate ... startDate endDate — last two dates are start/end
      base.start = dateToIso(tr[4]);
      base.end = dateToIso(tr[5]);
    }
    // Customer: "Adı Soyadı YILMAZ ÇİÇEK" or with double spaces "Adı Soyadı  YILMAZ  ÇİÇEK"
    const cm = raw.match(/Ad[iı]\s+Soyad[iı]\s+([A-ZÇĞİÖŞÜ][A-Za-zÇĞİıÖŞÜçğıöşü \-']{1,58})/);
    if (cm) {
      const v = cm[1].replace(/\s{2,}/g, " ").trim();
      if (v && !/^(Sigorta|Ünvanı|Unvani)/i.test(v)) base.customer = v.slice(0, 60);
    }
    // Brüt Prim can be on next line or same line
    const pm = raw.match(/Br[üu]t\s+Prim\s*\n?\s*([\d.,]+)/i);
    if (pm) base.premium = num(pm[1]);
    return base;
  },

  emaa: (raw, base) => {
    // Real EMAA layout — horizontal table:
    //   POLİÇE NO  EK BELGE NO  YENİLEME NO  BRANŞ  ACENTE NO  TANZİM TARİHİ  BAŞ. TARİHİ  BİTİŞ TARİHİ  SÜRE
    //   147874113  0  0  310  028038  12.09.2026  12.09.2026  12.09.2027  365
    const tr = raw.match(/POL[İI][ÇC]E\s+NO[\s\S]{0,200}?B[İI]T[İI][ŞS]\s+TAR[İI]H[İI]\s+S[ÜU]RE\s*\n\s*(\d+)\s+[\d.]+\s+[\d.]+\s+[\d.]+\s+[\d.]+\s+\d{2}\.\d{2}\.\d{4}\s+(\d{2}\.\d{2}\.\d{4})\s+(\d{2}\.\d{2}\.\d{4})/i);
    if (tr) {
      base.policeNo = tr[1];
      base.start = dateToIso(tr[2]);
      base.end = dateToIso(tr[3]);
    }
    // Customer: "ADI SOYADI/ ÜNVANI : HASAN OĞUZALP SAKALLI" — inline with colon
    const cm = raw.match(/ADI\s+SOYADI\s*\/?\s*[ÜU]?NVANI?\s*:\s*([^\n]+)/i);
    if (cm) {
      const v = cm[1].replace(/\s{2,}/g, " ").trim();
      if (v && !MARKETING_RE.test(v)) base.customer = v.slice(0, 60);
    }
    const brm = raw.match(/Br[üu]t\s+Prim\s*\n?\s*([\d.,]+)/i);
    if (brm) base.premium = num(brm[1]);
    return base;
  },

  allianz: (raw, base) => {
    // PDF.js flattens the right-column name ABOVE its "Adı Soyadı :" label:
    //   SİGORTALI / SİGORTA ETTİREN BİLGİLERİ
    //   Ali Ahmet İlhan
    //   Adı Soyadı :
    const nm = raw.match(/S[İI]GORTALI\s*\/\s*S[İI]GORTA\s+ETT[İI]REN\s+B[İI]LG[İI]LER[İI]\s*\n([^\n]{2,60})\s*\nAd[iı]\s+Soyad[iı]\s*:/i);
    if (nm) {
      const v = nm[1].replace(/\s{2,}/g, " ").trim();
      if (v && !MARKETING_RE.test(v) && !COMPANY_SIGNATURE_RE.test(v)) base.customer = v.slice(0, 60);
    }
    const pn = raw.match(/Poli[çc]e\s+No\s*:\s*([\d\-]{6,})/i)
      || raw.match(/Poli[çc]e\s+No\s*\n\s*([\d\-]{6,})/i);
    if (pn) base.policeNo = pn[1];
    // Dates on same line with times
    const dm = raw.match(/Ba[şs]lang[iı][çc]\s+Tarihi\s*:\s*(\d{2}\/\d{2}\/\d{4})[^\n]*?Biti[şs]\s+Tarihi\s*:\s*(\d{2}\/\d{2}\/\d{4})/i);
    if (dm) { base.start = dateToIso(dm[1]); base.end = dateToIso(dm[2]); }
    // Ödenecek Prim total — best signal is the PEŞİNAT row of the ÖDEME PLANI table:
    //   Ödeme Planı  Ödeme Vadesi  Ödenecek Prim
    //   PEŞİNAT :  16/09/2026  19,405.44 TL
    // This is always the FULL premium regardless of payment plan.
    const pesin = raw.match(/PE[ŞS][İI]NAT\s*:\s*\d{2}[./]\d{2}[./]\d{4}\s+([\d.,]+)\s*TL/i);
    if (pesin) {
      base.premium = num(pesin[1]);
    } else {
      // Fallback: PRİM BİLGİLERİ section — pick the LAST TL amount in the
      // "Garanti Fonu  Trafik Fonu  Ödenecek Prim" row (column at Ödenecek Prim).
      const primRow = raw.match(/Garanti\s+Fonu\s+Trafik\s+Fonu\s+[ÖO]denecek\s+Prim\s*\n([^\n]+)/i);
      if (primRow) {
        const tls = primRow[1].match(/[\d.,]+\s*TL\b/g) || [];
        if (tls.length) {
          const last = tls[tls.length - 1];
          base.premium = num(last.replace(/\s*TL/i, ""));
        }
      }
    }
    return base;
  },

  mapfre: (raw, base, fileName) => {
    // Mapfre PDF actually has extractable text on a later page. Real layout uses
    // stacked label→value newlines (no colon) and Turkish "BRÜT PRİM" all-caps
    // that the generic parser skips (no colon).
    if (!base.policeNo && fileName) {
      const fm = String(fileName).match(/(\d{10,})/);
      if (fm) base.policeNo = fm[1];
    }
    if (!base.branch) base.branch = "Trafik";
    // BRÜT PRİM  19.184,45  → same line, no colon
    const bp = raw.match(/BR[ÜU]T\s+PR[İI]M\s+([\d.,]+)/i);
    if (bp) base.premium = num(bp[1]);
    // Dates: "Poliçe Başlangıç Tarihi\n07/09/2026 12:00"
    if (!base.start) {
      const sm = raw.match(/Poli[çc]e\s+Ba[şs]lang[iı][çc]\s+Tarihi\s*\n\s*(\d{2}\/\d{2}\/\d{4})/i);
      if (sm) base.start = dateToIso(sm[1]);
    }
    if (!base.end) {
      const em = raw.match(/Poli[çc]e\s+Biti[şs]\s+Tarihi\s*\n\s*(\d{2}\/\d{2}\/\d{4})/i);
      if (em) base.end = dateToIso(em[1]);
    }
    return base;
  },
};


export function parsePolicyText(text, branchList, fileName = "") {
  const rawText = text || "";
  if (!rawText.trim()) {
    return {
      policeNo: "",
      customer: "",
      branch: branchList[0]?.name || "",
      company: "",
      plate: "",
      start: "",
      end: "",
      premium: "",
      commissionRate: "",
      payment: "Bekliyor",
      note: `PDF'den aktarıldı · ${fileName} · metin çıkarılamadı, alanları elle doldurun`,
      _rawText: "",
    };
  }

  // Detect company on FULL text (marketing preface often contains the brand name — that's fine).
  const company = detectCompany(rawText);

  // Skip marketing preface for the data extraction. Company header, brand, sadakat program etc.
  // are typically before markers like "Poliçe No", "Başlama Tarihi" etc.
  const dataText = skipPreface(rawText);

  const policeNoRaw = findAfter(dataText, COMMON_LABELS.policeNo, 40);
  const policeNo = (policeNoRaw.split(/\s+/)[0] || "").replace(/[^0-9A-Za-z\-\/]/g, "").slice(0, 30);

  const customerRaw = findAfter(dataText, COMMON_LABELS.customer, 80, (v) => MARKETING_RE.test(v) || COMPANY_SIGNATURE_RE.test(v));
  const customer = customerRaw
    .split(/\s+(?=(?:Sigorta|Ürün|Urun|Başlangıç|Baslangic|Bitiş|Bitis|Başlama|Baslama|Brüt|Brut|Ödeme|Odeme|Poliçe|Police|T\.C\.|TCK?N|Vergi|Adres|Tel|Cep|E-?posta|Marka|Motor|Plaka)\b)/i)[0]
    .replace(/[|:;,]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim()
    .slice(0, 60);

  const premiumRaw = findAfter(dataText, COMMON_LABELS.premium, 40);
  const premium = num(premiumRaw);

  // Prefer BAŞLANGICI VE BİTİMİ range (has both dates on one line, common in Doğa receipts)
  let start = "";
  let end = "";
  const rangeRaw = findAfter(dataText, COMMON_LABELS.datesRange, 40);
  if (rangeRaw) {
    const found = findDates(rangeRaw);
    if (found[0]) start = dateToIso(found[0]);
    if (found[1]) end = dateToIso(found[1]);
  }
  if (!start || !end) {
    // Fallback: use labelled dates directly
    const startLabelRaw = findAfter(dataText, ["Başlama Tarihi", "Başlangıç Tarihi", "Baslama Tarihi", "Baslangic Tarihi", "Başlangıç"], 30);
    const endLabelRaw = findAfter(dataText, ["Bitiş Tarihi", "Vade Bitiş", "Bitis Tarihi", "Bitiş"], 30);
    const s1 = findDates(startLabelRaw)[0];
    const e1 = findDates(endLabelRaw)[0];
    if (s1) start = dateToIso(s1);
    if (e1) end = dateToIso(e1);
  }
  if (!start || !end) {
    // Final fallback: first two dates in the data section
    const dates = findDates(dataText);
    if (!start && dates[0]) start = dateToIso(dates[0]);
    if (!end && dates[1]) end = dateToIso(dates[1]);
  }

  // Branch is virtually always mentioned in the title/header — restrict detection
  // to the first 500 chars to avoid boilerplate matches (e.g. a Kasko policy's
  // legal notes mention "Karayolları Trafik" which would misclassify as Trafik).
  const branch = detectBranch(rawText.slice(0, 500), branchList);
  const companyName = company?.name || findAfter(dataText, COMMON_LABELS.company, 60);

  const base = {
    policeNo,
    customer,
    branch,
    company: companyName,
    plate: extractPlate(dataText),
    start,
    end,
    premium,
    commissionRate: "",
    payment: "Bekliyor",
    note: `PDF'den aktarıldı · ${fileName}${company ? ` · ${company.name} şablonu` : " · genel şablon"}`,
    _rawText: rawText.slice(0, 4000),
  };

  // Company-specific overrides fix known-tricky layouts (multi-column tables,
  // stacked colon-prefixed values, right-column names, image-only covers, etc.).
  const override = company && COMPANY_OVERRIDES[company.id];
  if (override) {
    try {
      override(rawText, base, fileName);
    } catch (e) {
      // Non-fatal — fall back to generic values already in `base`.
    }
  }

  return base;
}
